import { useState } from 'react';
import { Link } from 'react-router';
import { Mail01, ArrowLeft, CheckCircle, XCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useTranslation } from 'react-i18next';
import { VersionFooter } from '@/components/base/version-footer/version-footer';
import { requestPasswordReset } from '@/data/api/auth';

export const ResetPasswordPage = () => {
    const { t } = useTranslation(['pages:auth', 'common']);
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendSuccess, setResendSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await requestPasswordReset(email);
            setIsSubmitted(true);
        } catch (err: any) {
            setError(err.message || t('resetPassword.errorRequest'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setResendSuccess(false);
        setIsResending(true);

        try {
            await requestPasswordReset(email);
            setResendSuccess(true);
        } catch (err: any) {
            setError(err.message || t('resetPassword.errorResend'));
        } finally {
            setIsResending(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="flex justify-center items-center gap-2 mb-8 hover:opacity-80 transition-opacity">
                        <img src="/images/logo-full.svg" alt="Eve Mind" className="h-14 w-auto sm:h-16" />
                    </Link>

                    {/* Success Message */}
                    <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary text-center">
                        <div className="flex justify-center mb-4">
                            <CheckCircle className="size-12 text-success-primary" />
                        </div>

                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('resetPassword.successTitle')}
                        </h1>

                        <p className="text-sm text-tertiary mb-6">
                            {t('resetPassword.successMessage')}{' '}
                            <strong>{email}</strong>. {t('resetPassword.successInstructions')}
                        </p>

                        {error && (
                            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-700 flex items-center gap-2">
                                <XCircle className="size-5" />
                                {error}
                            </div>
                        )}

                        {resendSuccess && (
                            <div className="mb-4 rounded-lg bg-success-50 p-3 text-sm text-success-700 flex items-center gap-2">
                                <CheckCircle className="size-5" />
                                {t('resetPassword.resendSuccess')}
                            </div>
                        )}

                        <div className="space-y-3">
                            <Link
                                to="/auth/login"
                                className="inline-flex items-center justify-center rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover w-full"
                            >
                                {t('resetPassword.backToLoginButton')}
                            </Link>

                            <Button
                                onClick={handleResend}
                                size="md"
                                color="secondary"
                                className="w-full"
                                isLoading={isResending}
                            >
                                {t('resetPassword.sendAnotherEmail')}
                            </Button>
                        </div>
                    </div>
                </div>
                <VersionFooter />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="mb-8 flex justify-center">
                    <img src="/images/logo-full.svg" alt="Eve Mind" className="h-14 w-auto sm:h-16" />
                </div>

                {/* Reset Password Form */}
                <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary mb-4">
                    <div className="mb-6 text-center">
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('resetPassword.title')}
                        </h1>
                        <p className="text-sm text-tertiary">
                            {t('resetPassword.subtitle')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <Input
                            type="email"
                            label={t('resetPassword.emailLabel')}
                            placeholder={t('resetPassword.emailPlaceholder')}
                            value={email}
                            onChange={setEmail}
                            icon={Mail01}
                            isRequired
                            size="md"
                        />

                        {error && (
                            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 flex items-center gap-2">
                                <XCircle className="size-5" />
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            size="lg"
                            color="primary"
                            isLoading={isLoading}
                            className="w-full"
                        >
                            {t('resetPassword.submitButton')}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/auth/login"
                            className="inline-flex items-center text-sm text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            <ArrowLeft className="size-4 mr-1" />
                            {t('resetPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
                <VersionFooter />
            </div>
        </div>
    );
};
